/**
 * Account Linking Service
 * Handles linking Google accounts to existing email/password accounts
 */

import {
  linkWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  signInWithCredential,
  signInWithRedirect,
  getAuth
} from 'firebase/auth';
import Firebase from '../Firebase.js';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../Firebase.js';

class AccountLinkingService {
  constructor() {
    this.auth = getAuth(Firebase);
    this.db = db;
    this.googleProvider = new GoogleAuthProvider();
  }

  /**
   * Link Google account to current user
   * @param {Object} currentUser - The currently signed-in user
   * @returns {Promise<void>}
   */
  async linkGoogleAccount(currentUser) {
    try {
      if (!currentUser) {
        throw new Error('No user is currently signed in');
      }

      // Link with redirect - this will redirect to Google
      await linkWithRedirect(currentUser, this.googleProvider);

    } catch (error) {
      console.error('Error linking Google account:', error);

      // Handle specific Firebase errors
      if (error.code === 'auth/credential-already-in-use') {
        throw new Error('This Google account is already linked to another user account. Please try signing in with Google directly.');
      } else if (error.code === 'auth/provider-already-linked') {
        throw new Error('Google account is already linked to this user.');
      } else if (error.code === 'auth/requires-recent-login') {
        throw new Error('Please re-authenticate and try again.');
      } else {
        throw new Error(error.message || 'Failed to link Google account');
      }
    }
  }

  /**
   * Handle redirect result after Google authentication
   * @returns {Promise<Object>} - Result of the linking operation
   */
  async handleRedirectResult() {
    try {
      console.log('Handling redirect result from Google authentication');

      const result = await getRedirectResult(this.auth);

      if (result) {
        const user = result.user;
        const credential = GoogleAuthProvider.credentialFromResult(result);

        // Update user profile in Firestore with Google info
        await this.updateUserProfileWithGoogleData(user);

        return {
          success: true,
          user: user,
          credential: credential,
          isNewUser: false, // This is linking, not new user
          message: 'Google account linked successfully!'
        };
      } else {
        return {
          success: false,
          cancelled: true,
          message: 'Google authentication was cancelled'
        };
      }
    } catch (error) {
      // Handle account merging scenario
      if (error.code === 'auth/account-exists-with-different-credential') {
        const pendingCredential = GoogleAuthProvider.credentialFromError(error);
        const email = error.customData.email;

        return await this.handleAccountMerging(email, pendingCredential);
      } else {
        throw new Error(error.message || 'Failed to complete Google authentication');
      }
    }
  }

  /**
   * Handle account merging when Google account exists with different credential
   * @param {string} email - Email address
   * @param {Object} pendingCredential - Pending Google credential
   * @returns {Promise<Object>} - Merge result
   */
  async handleAccountMerging(email, pendingCredential) {
    try {
      console.log('Handling account merging for email:', email);

      // Get the existing user data
      const existingUserData = await this.getUserDataByEmail(email);

      if (!existingUserData) {
        throw new Error('Unable to find existing user account for merging');
      }

      return {
        success: false,
        requiresMerge: true,
        email: email,
        pendingCredential: pendingCredential,
        existingUserData: existingUserData,
        message: 'This Google account is linked to another user. Would you like to merge the accounts?'
      };
    } catch (error) {
      console.error('Error handling account merging:', error);
      throw new Error('Failed to handle account merging: ' + error.message);
    }
  }

  /**
   * Complete account merging after user confirmation
   * @param {Object} mergeData - Data from the merge request
   * @returns {Promise<Object>} - Merge result
   */
  async completeAccountMerging(mergeData) {
    try {
      const { email, pendingCredential, existingUserData } = mergeData;

      console.log('Completing account merging for email:', email);

      // Sign in with the existing account first
      // This is a simplified version - in practice, you'd need to handle
      // the existing account authentication flow

      // For now, we'll throw an error indicating this needs to be handled
      // in the UI with proper user authentication flow
      throw new Error('Account merging requires additional user authentication. Please contact support.');

    } catch (error) {
      console.error('Error completing account merging:', error);
      throw new Error('Failed to complete account merging: ' + error.message);
    }
  }

  /**
   * Update user profile with Google data
   * @param {Object} user - Firebase user object
   * @returns {Promise<void>}
   */
  async updateUserProfileWithGoogleData(user) {
    try {
      const userRef = doc(this.db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();

        // Update with Google provider data
        const googleProviderData = user.providerData.find(provider => provider.providerId === 'google.com');

        if (googleProviderData) {
          const updateData = {
            googleLinked: true,
            googleEmail: googleProviderData.email,
            googleDisplayName: googleProviderData.displayName,
            googlePhotoURL: googleProviderData.photoURL,
            updatedAt: new Date()
          };

          await updateDoc(userRef, updateData);
          console.log('User profile updated with Google data');
        }
      }
    } catch (error) {
      console.error('Error updating user profile with Google data:', error);
      // Don't throw - this is not critical for the linking process
    }
  }

  /**
   * Get user data by email from Firestore
   * @param {string} email - Email address
   * @returns {Promise<Object|null>} - User data or null
   */
  async getUserDataByEmail(email) {
    try {
      // This is a simplified version - in practice, you'd need to query
      // Firestore by email, but Firestore doesn't support direct email queries
      // You'd need to maintain an email-to-UID mapping or use Firebase Auth

      console.log('Getting user data by email:', email);

      // For now, return null - this would need to be implemented based on your data structure
      return null;
    } catch (error) {
      console.error('Error getting user data by email:', error);
      return null;
    }
  }

  /**
   * Check if user has Google account linked
   * @param {Object} user - Firebase user object
   * @returns {boolean} - True if Google is linked
   */
  isGoogleLinked(user) {
    if (!user || !user.providerData) {
      return false;
    }

    return user.providerData.some(provider => provider.providerId === 'google.com');
  }

  /**
   * Get linked providers for user
   * @param {Object} user - Firebase user object
   * @returns {Array} - Array of linked provider IDs
   */
  getLinkedProviders(user) {
    if (!user || !user.providerData) {
      return [];
    }

    return user.providerData.map(provider => provider.providerId);
  }

  /**
   * Sign in with Google (for new users or direct Google sign-in)
   * @returns {Promise<Object>} - Sign-in result
   */
  async signInWithGoogle() {
    try {
      console.log('Starting Google sign-in');
      await signInWithRedirect(this.auth, this.googleProvider);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw new Error(error.message || 'Failed to sign in with Google');
    }
  }
}

export default new AccountLinkingService();
